"use client";

import { useEffect, useState } from "react";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Input, Button, Checkbox } from "ui";
import loginBg from "@/assets/images/loginBg.jpg";
import loginOBg from "@/assets/images/loginOBg.jpeg";
import { toast } from "@/lib/sonner";
import { signIn } from "@sajilni/auth/react";
import { useLocale, useTranslation } from "@sajilni/localization";
import { useRouter, useSearchParams } from "next/navigation";
import { Logo } from "../Logo";
import AuthAudienceToggle from "@/components/auth/AuthAudienceToggle";
import { getLoginTitleKey, resolveAuthAudience } from "@/lib/auth-flow-params";
import { USER_TYPES } from "@sajilni/config/constants";
import { getLoggedInUserType } from "@/lib/actions/logedInUserTryp";
import { ParticipantType } from "database";
import { Loader2, X } from "lucide-react";
import Link from "next/link";

const createFormSchema = (t: (key: string) => string) =>
  z.object({
    email: z
      .string()
      .min(1, { message: t("errors.emailRequired") })
      .email({ message: t("errors.invalidEmail") }),
    password: z
      .string()
      .min(1, { message: t("errors.passwordRequired") })
      .min(8, { message: t("errors.passwordLength") }),
    rememberMe: z.boolean().default(false),
  });

export default function LoginForm({
  type,
  forgetPasswordRedirect,
  signupHref,
  signupLabel,
  beforeLogin,
  token,
  eventId,
  eventSlug,
}: {
  type: USER_TYPES;
  forgetPasswordRedirect?: string;
  signupHref?: string;
  signupLabel?: string;
  beforeLogin?: (email: string) => Promise<void>;
  token?: string;
  eventId?: string;
  eventSlug?: string;
}) {
  const locale = useLocale();
  const isRtl = locale === "ar";

  const { t } = useTranslation("sajilni2.0");
  const searchParams = useSearchParams();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const formSchema = createFormSchema(t);
  type FormValues = z.input<typeof formSchema>;

  // Read error from URL searchParams
  useEffect(() => {
    if (!searchParams) return;

    // Get all error parameters (NextAuth might add multiple)
    const errors = searchParams.getAll("error");
    if (errors.length > 0) {
      // Find the first non-empty error message
      const error =
        errors.find((e) => e && e.trim() !== "") || errors[errors.length - 1];
      if (error) {
        // Decode URL-encoded error message
        const decodedError = decodeURIComponent(error);
        setErrorMessage(decodedError);

        // Clear error from URL after displaying
        if (typeof window !== "undefined") {
          const url = new URL(window.location.href);
          // Remove all error parameters
          url.searchParams.delete("error");
          window.history.replaceState({}, "", url.toString());
        }
      }
    }
  }, [searchParams]);

  const router = useRouter();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const login = async (data: FormValues) => {
    try {
      let userType: Awaited<ReturnType<typeof getLoggedInUserType>> | null =
        null;
      if (eventSlug) {
        userType = await getLoggedInUserType(data.email, undefined, eventSlug);
      }
      const callbackUrl = searchParams?.get("callbackUrl") || undefined;
      const result = await signIn("credentials", {
        email: data.email,
        password: data.password,
        rememberMe: data.rememberMe,
        ...(eventSlug && { eventSlug: eventSlug }),
        redirect: false,
        ...(callbackUrl ? { callbackUrl } : {}),
      });

      if (result?.error) {
        toast.error("password or email is incorrect");
        return;
      }

      if (userType && !("error" in userType) && userType.type && userType.id) {
        localStorage.setItem(
          "participantData",
          JSON.stringify({
            type: userType.type as ParticipantType,
            id: userType.id,
          })
        );
      }

      window.location.href = result?.url || callbackUrl || "/";
    } catch {
      toast.error("password or email is incorrect");
    }
  };

  const onSubmit = async (data: FormValues) => {
    if (beforeLogin) {
      await beforeLogin(data.email).then(() => login(data));
    } else {
      login(data);
    }
  };
  useEffect(() => {
    if (!token) return;

    (async () => {
      try {
        const res = await fetch("/api/auth/verify-invitation-jwt", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const data = await res.json();
        if (!res.ok || !data?.payload) {
          router.push("/");
          return;
        }
        const decoded = data.payload as { email?: string; userId?: string };
        const email = decoded.email;
        if (!email) {
          router.push("/");
          return;
        }

        if (eventSlug) {
          const userType = await getLoggedInUserType(
            email,
            undefined,
            eventSlug
          );
          const result = await signIn("credentials", {
            email,
            token,
            rememberMe: true,
            eventSlug,
            redirect: false,
          });
          if (result?.error) {
            router.push("/");
            return;
          }
          if (!("error" in userType) && userType.type && userType.id) {
            localStorage.setItem(
              "participantData",
              JSON.stringify({
                type: userType.type as ParticipantType,
                id: userType.id,
              })
            );
            fetch(`/api/participant-invitation`, {
              method: "POST",
              body: JSON.stringify({
                entityId: userType.id,
                email,
                entityType: userType.type,
              }),
            });
          }
          window.location.href = result?.url || "/";
        } else {
          const result = await signIn("credentials", {
            email,
            token,
            rememberMe: true,
            redirect: false,
          });
          if (result?.error) {
            router.push("/");
            return;
          }
          window.location.href = result?.url || "/";
        }
      } catch {
        router.push("/");
      }
    })();
  }, [token]);

  // Add global "Enter" key listener
  useEffect(() => {
    const handleEnterKey = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        form.handleSubmit(onSubmit)();
      }
    };

    window.addEventListener("keydown", handleEnterKey);
    return () => {
      window.removeEventListener("keydown", handleEnterKey);
    };
  }, [form, onSubmit]);

  const audience = resolveAuthAudience(
    searchParams?.get("type") ?? (type ? String(type) : undefined)
  );
  const showAudienceToggle = !eventId && audience !== "participant";

  // Magic-link auto-login: skip the login form so it doesn't flash before redirect.
  if (token) {
    return (
      <div
        dir={isRtl ? "rtl" : "ltr"}
        className="notranslate flex min-h-screen w-full items-center justify-center p-6"
        aria-busy="true"
        aria-live="polite"
      >
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="notranslate grid h-full min-h-screen w-full grid-cols-1 md:grid-cols-2"
    >
      <div className="flex h-full flex-col justify-between p-4 sm:p-6 md:p-8">
        <div className="mb-4 md:mb-0">
          <Logo
            endComponent={
              showAudienceToggle ? (
                <AuthAudienceToggle current={audience} basePath="/login" />
              ) : undefined
            }
          />
        </div>
        <div className="flex h-full flex-col items-center justify-center gap-4 md:gap-8">
          <div className="mx-auto flex w-full max-w-sm flex-col items-start gap-2 px-2 sm:px-4 md:gap-3 md:px-0">
            <div className="text-2xl font-semibold md:text-3xl">
              {t(getLoginTitleKey(audience, String(type)))}
            </div>
            <div className="text-sm font-medium text-gray-600 md:text-base">
              {t("descriptions.login")}
            </div>
          </div>
          <div className="mx-auto flex w-full max-w-sm flex-col gap-3 px-2 sm:px-4 md:gap-4 md:px-0">
            {errorMessage && (
              <div className="relative w-full rounded-lg border border-red-500 bg-[#FCE4E4] p-4">
                <button
                  onClick={() => setErrorMessage(null)}
                  className="absolute right-2 top-2 text-red-500 transition-colors hover:text-red-700"
                  aria-label="Close error"
                >
                  <X className="h-4 w-4" />
                </button>
                <p className="pr-6 text-sm font-medium text-red-600">
                  {errorMessage}
                </p>
              </div>
            )}
            <div className="flex flex-col gap-1 md:gap-[6px]">
              <div className="text-sm font-medium text-gray-700">
                {t("labels.email")}
              </div>
              <Input
                type="email"
                placeholder={t("Enter email")}
                {...form.register("email")}
                className="w-full"
              />
              {form.formState.errors.email && (
                <div className="text-xs text-red-500 md:text-sm">
                  {form.formState.errors.email.message}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-1 md:gap-[6px]">
              <div className="text-sm font-medium text-gray-700">
                {t("labels.password")}
              </div>
              <Input
                type="password"
                placeholder={t("Enter password")}
                {...form.register("password")}
                className="w-full"
              />
              {form.formState.errors.password && (
                <div className="text-xs text-red-500 md:text-sm">
                  {form.formState.errors.password.message}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-xs font-medium text-gray-700 sm:text-sm">
                <Checkbox
                  id="rememberMe"
                  onCheckedChange={(e) => {
                    form.setValue("rememberMe", !form.getValues("rememberMe"));
                  }}
                />
                <span className="whitespace-nowrap">
                  {t("labels.rememberMe")}
                </span>
              </div>
              <Link
                href={forgetPasswordRedirect ?? `/forgot-password?type=${type}`}
                className="text-primary-700 hover:text-primary cursor-pointer text-xs font-medium sm:text-sm"
              >
                {t("links.forgotPassword")}
              </Link>
            </div>

            <Button
              className="bg-primary-500 hover:bg-primary-600 mt-2 w-full text-white"
              onClick={(e) => {
                e.preventDefault();
                form.handleSubmit(onSubmit)();
              }}
            >
              {t("labels.signIn")}
            </Button>

            {signupHref ? (
              <div className="flex w-full flex-row items-center justify-center gap-2 pt-3 md:pt-4">
                <span className="text-xs font-medium text-gray-700 md:text-sm">
                  {t("titles.donotHaveAccount")}
                </span>
                <Link
                  href={signupHref}
                  className="text-primary-700 text-xs font-medium md:text-sm"
                >
                  {signupLabel ?? t("titles.signUp")}
                </Link>
              </div>
            ) : null}
          </div>
        </div>
        <div className="mt-6 flex flex-col items-center gap-2 text-center text-xs text-gray-600 md:mt-0 md:flex-row md:items-center md:justify-between md:text-left md:text-sm">
          <div className="md:whitespace-nowrap">
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </div>
          <nav className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 md:flex-nowrap md:gap-x-3">
            <Link
              href="https://sajilni.events/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary-700"
            >
              {t("footer.home")}
            </Link>
            <span className="text-gray-300">|</span>
            <Link
              href="https://sajilni.events/about/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary-700"
            >
              {t("footer.about")}
            </Link>
            <span className="text-gray-300">|</span>
            <Link
              href="https://sajilni.events/contact/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary-700"
            >
              {t("footer.contactUs")}
            </Link>
            <span className="text-gray-300">|</span>
            <Link
              href="https://sajilni.events/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary-700"
            >
              {t("footer.privacyPolicy")}
            </Link>
            <span className="text-gray-300">|</span>
            <Link
              href="https://sajilni.events/terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary-700"
            >
              {t("footer.termsOfService")}
            </Link>
          </nav>
        </div>
      </div>

      <div
        className={`bg hidden bg-cover ${
          type == USER_TYPES.ADMIN ? "bg-right-top" : "bg-center"
        } md:block ltr:rounded-l-[20px] rtl:rounded-r-[20px]`}
        style={{
          backgroundImage: `url(${
            type == USER_TYPES.ADMIN ? loginBg.src : loginOBg.src
          })`,
        }}
      ></div>
    </div>
  );
}
